import { Service } from "dioc"
import { computed, ref, watch } from "vue"
import { useIntervalFn } from "@vueuse/core"
import * as E from "fp-ts/Either"
import { runGQLQuery } from "@hoppscotch/common/helpers/backend/GQLClient"
import { useStreamStatic } from "@hoppscotch/common/composables/stream"
import { platform } from "@hoppscotch/common/platform"
import { DEV_FLAGS } from "@app/config/dev-flags"
import {
  getOrganizationDomain,
  isDefaultCloudInstance,
} from "@app/helpers/utils/cloud-for-orgs"
import {
  GetMyOrganizationsDocument,
  GetMyOrganizationsQuery,
  OrganizationStatus,
} from "@app/helpers/backend/graphql"
import { validateOrgDomain } from "@app/helpers/validation/domain-validation"
import { PersistenceService } from "@services/persistence"
import { getService } from "@hoppscotch/common/modules/dioc"

// Mock data is gated behind import.meta.env.DEV so Vite can statically
// eliminate it from production builds. Wrapped in a function so the array
// is never allocated in prod even if tree-shaking misses a top-level const.
const getMockOrganizations = (): GetMyOrganizationsQuery["myOrganizations"] => {
  if (!import.meta.env.DEV) return []
  return [
    {
      organization: {
        orgID: "mock-org-1",
        orgName: "org4",
        orgDomain: "org4",
        orgStatus: OrganizationStatus.Active,
        orgLogo: null,
      },
      isAdmin: true,
      invitedOn: new Date().toISOString(),
      membershipID: "mock-membership-1",
    },
    {
      organization: {
        orgID: "mock-org-2",
        orgName: "org1",
        orgDomain: "org1",
        orgStatus: OrganizationStatus.Active,
        orgLogo: null,
      },
      isAdmin: false,
      invitedOn: new Date().toISOString(),
      membershipID: "mock-membership-2",
    },
    {
      organization: {
        orgID: "mock-org-3",
        orgName: "flipkart",
        orgDomain: "flipkart",
        orgStatus: OrganizationStatus.Active,
        orgLogo: null,
      },
      isAdmin: true,
      invitedOn: new Date().toISOString(),
      membershipID: "mock-membership-3",
    },
    {
      organization: {
        orgID: "mock-org-4",
        orgName: "Amazon",
        orgDomain: "amazon",
        orgStatus: OrganizationStatus.Active,
        orgLogo: null,
      },
      isAdmin: false,
      invitedOn: new Date().toISOString(),
      membershipID: "mock-membership-4",
    },
    {
      organization: {
        orgID: "mock-org-5",
        orgName: "ford",
        orgDomain: "ford",
        orgStatus: OrganizationStatus.Active,
        orgLogo: null,
      },
      isAdmin: false,
      invitedOn: new Date().toISOString(),
      membershipID: "mock-membership-5",
    },
    {
      organization: {
        orgID: "mock-org-6",
        orgName: "test2",
        orgDomain: "test2",
        orgStatus: OrganizationStatus.Inactive,
        orgLogo: null,
      },
      isAdmin: false,
      invitedOn: new Date().toISOString(),
      membershipID: "mock-membership-6",
    },
  ]
}

type MyOrganizationMembership =
  GetMyOrganizationsQuery["myOrganizations"][number]

type OrganizationSwitcherItem = MyOrganizationMembership & {
  isActive: boolean
}

export type SwitcherItem = OrganizationSwitcherItem & { type: "organization" }

const REMEMBERED_ORG_DOMAIN_KEY = "REMEMBERED_ORG_DOMAIN"
const DEFAULT_POLLING_INTERVAL = 10000

export class OrganizationSwitcherService extends Service {
  public static readonly ID = "ORGANIZATION_SWITCHER_SERVICE"

  private myOrganizationsRef = ref<MyOrganizationMembership[]>([])

  // Starts true to prevent empty state flicker during auth init
  private isLoadingRef = ref(true)
  private errorRef = ref<string | null>(null)

  // Moved from useLocalStorage (which writes to window.localStorage and leaks
  // across WebKit instances on desktop) to a plain ref backed by the
  // persistence service. The persistence service writes through the kernel
  // store which lives on disk outside WebKit, so the remembered domain is
  // scoped to the current webview host.
  private rememberedOrgDomain = ref<string | null>(null)

  // Populated synchronously from localStorage during auth init
  private probableUser = useStreamStatic(
    platform.auth.getProbableUserStream(),
    platform.auth.getProbableUser(),
    () => {
      /* noop */
    }
  )[0]

  // Waits for Firebase auth token before populating
  private currentUser = useStreamStatic(
    platform.auth.getCurrentUserStream(),
    platform.auth.getCurrentUser(),
    () => {
      /* noop */
    }
  )[0]

  private pollingEnabled = ref(false)
  private pollingControl = useIntervalFn(
    () => this.fetchMyOrganizations(true),
    DEFAULT_POLLING_INTERVAL,
    { immediate: false }
  )
  private pausePolling = this.pollingControl.pause
  private resumePolling = this.pollingControl.resume

  public readonly myOrganizations = computed(
    () => this.myOrganizationsRef.value
  )

  public readonly isLoading = computed(() => this.isLoadingRef.value)

  public readonly error = computed(() => this.errorRef.value)

  public readonly currentOrgDomain = getOrganizationDomain()

  public readonly isOnDefaultInstance = isDefaultCloudInstance

  public readonly switcherItems = computed<SwitcherItem[]>(() => {
    const currentDomain = this.currentOrgDomain

    return this.myOrganizationsRef.value.map((membership) => ({
      type: "organization" as const,
      ...membership,
      isActive: membership.organization.orgDomain === currentDomain,
    }))
  })

  override async onServiceInit() {
    // Restore remembered org domain from persistence service (kernel store).
    // This replaces the old useLocalStorage approach which leaked across WebKit
    // instances on desktop.
    const persistenceService = getService(PersistenceService)
    const stored = await persistenceService.getLocalConfig(
      REMEMBERED_ORG_DOMAIN_KEY
    )
    if (stored) {
      this.rememberedOrgDomain.value = stored
    }

    // TEST MODE: Skips all the auth watching logic and just loads mock data immediately.
    // This makes it easier to test the UI without needing to set up Firebase auth or a
    // backend.
    if (DEV_FLAGS.USE_MOCK_ORGANIZATIONS) {
      await this.fetchMyOrganizations()
      return
    }

    watch(
      this.currentUser,
      async (user) => {
        if (!user) {
          this.myOrganizationsRef.value = []

          // Check probableUser to prevent empty state flicker on hard reload
          if (!this.probableUser.value) {
            this.isLoadingRef.value = false
          }

          this.pausePolling()
          return
        }

        await this.fetchMyOrganizations()

        if (this.pollingEnabled.value) {
          this.resumePolling()
        }
      },
      { immediate: true }
    )
  }

  public async fetchMyOrganizations(silent = false) {
    // TEST MODE: Returns mock data immediately instead of making an API call. The silent
    // parameter is ignored in test mode since there's no actual loading happening.
    if (DEV_FLAGS.USE_MOCK_ORGANIZATIONS) {
      this.myOrganizationsRef.value = getMockOrganizations()
      this.isLoadingRef.value = false
      return
    }

    if (!this.currentUser.value) {
      return
    }

    if (!silent) {
      this.isLoadingRef.value = true
    }
    this.errorRef.value = null

    try {
      await platform.auth.waitProbableLoginToConfirm()

      const result = await runGQLQuery<
        GetMyOrganizationsQuery,
        Record<string, never>,
        string
      >({
        query: GetMyOrganizationsDocument,
        variables: {},
      })

      if (E.isRight(result)) {
        this.myOrganizationsRef.value = result.right.myOrganizations || []
        this.errorRef.value = null
      } else {
        const errorMsg =
          result.left.error instanceof Error
            ? result.left.error.message
            : typeof result.left.error === "string"
              ? result.left.error
              : JSON.stringify(result.left.error)

        if (
          !errorMsg.includes("Unauthorized") &&
          !errorMsg.includes("Forbidden")
        ) {
          this.errorRef.value = errorMsg
        }
        console.error("Failed to fetch organizations:", result.left.error)
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error"
      if (
        !errorMsg.includes("Unauthorized") &&
        !errorMsg.includes("Forbidden")
      ) {
        this.errorRef.value = errorMsg
      }
      console.error("Error fetching organizations:", err)
    } finally {
      this.isLoadingRef.value = false
    }
  }

  public async switchToOrganization(orgDomain: string | null) {
    if (orgDomain !== null) {
      const validationError = validateOrgDomain(orgDomain)
      if (validationError) {
        this.errorRef.value = validationError
        console.error(validationError)
        return
      }
    }

    this.rememberedOrgDomain.value = orgDomain

    // persist to the per-instance store so the current webview can restore
    // the value on reload
    const persistenceService = getService(PersistenceService)
    if (orgDomain !== null) {
      await persistenceService.setLocalConfig(
        REMEMBERED_ORG_DOMAIN_KEY,
        orgDomain
      )
    } else {
      await persistenceService.removeLocalConfig(REMEMBERED_ORG_DOMAIN_KEY)
    }

    // the unified store write that was here previously is no longer needed.
    // org context is now passed via URL query param (?org=test-org.hoppscotch.io)
    // when the appload plugin creates the new webview, so there's no cross-webview
    // communication needed for org domain detection

    if (platform.organization?.switchToInstance) {
      await platform.organization.switchToInstance(orgDomain)
      return
    }

    const rootDomain = platform.organization?.getRootDomain() ?? "hoppscotch.io"
    const targetUrl = orgDomain
      ? `${window.location.protocol}//${orgDomain}.${rootDomain}`
      : `${window.location.protocol}//${rootDomain}`

    window.location.href = targetUrl
  }

  public getRememberedOrgDomain(): string | null {
    return this.rememberedOrgDomain.value
  }

  public enablePolling() {
    this.pollingEnabled.value = true
    if (this.currentUser.value) {
      this.resumePolling()
    }
  }

  public disablePolling() {
    this.pollingEnabled.value = false
    this.pausePolling()
  }

  public hasOrganizations(): boolean {
    return this.myOrganizationsRef.value.length > 0
  }

  public isAdminOfCurrentOrg(): boolean {
    const currentDomain = this.currentOrgDomain
    if (!currentDomain) return false

    const membership = this.myOrganizationsRef.value.find(
      (m) => m.organization.orgDomain === currentDomain
    )

    return membership?.isAdmin ?? false
  }
}
